import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getAppPortalRoot } from "../utils/appPortal";
import { X } from "lucide-react";
import {
  DEFAULT_NOTES_PREFERENCES,
  NOTES_LIST_DISPLAY_HELP,
  NOTES_LIST_DISPLAY_LABELS,
  NOTES_LIST_DISPLAY_MODES,
  NOTES_SCROLL_ANCHOR_LABELS,
  NOTES_SCROLL_ANCHORS,
  NOTES_SORT_ORDER_HELP,
  NOTES_SORT_ORDER_LABELS,
  NOTES_SORT_ORDERS,
  sanitizeNotesPreferences,
} from "../constants/notesPreferences";
import "./DeleteWordModal.css";
import "./RegisterSettingsModal.css";
import "./EntrainementVoc.css";

function RadioChip({ name, label, checked, onChange }) {
  return (
    <label
      className={`entrainement-chip entrainement-chip--round${
        checked ? " entrainement-chip--checked" : ""
      }`}
    >
      <input
        type="radio"
        className="entrainement-chip__input"
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <span className="entrainement-chip__marker" aria-hidden="true" />
      <span className="entrainement-chip__label">{label}</span>
    </label>
  );
}

export default function NotesSettingsModal({ open, value, onChange, onClose }) {
  const [draft, setDraft] = useState(DEFAULT_NOTES_PREFERENCES);

  useEffect(() => {
    if (!open) return;
    setDraft(sanitizeNotesPreferences(value));
  }, [open, value]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const handleApply = () => {
    onChange?.(sanitizeNotesPreferences(draft));
    onClose?.();
  };

  const handleReset = () => {
    onChange?.({
      ...DEFAULT_NOTES_PREFERENCES,
      categoryFilter: sanitizeNotesPreferences(value).categoryFilter,
    });
    onClose?.();
  };

  const displayHelp = NOTES_LIST_DISPLAY_HELP[draft.listDisplayMode];
  const sortHelp = NOTES_SORT_ORDER_HELP[draft.sortOrder];

  return createPortal(
    <div
      className="delete-word-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="delete-word-modal register-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notes-settings-modal-title"
      >
        <button
          type="button"
          className="delete-word-modal__close"
          onClick={onClose}
          aria-label="Fermer"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <h3
          id="notes-settings-modal-title"
          className="delete-word-modal__title delete-word-modal__title--gothic"
        >
          Paramètres du carnet de notes
        </h3>

        <div className="register-settings-modal__body">
          <section className="register-settings-modal__section">
            <h4 className="register-settings-modal__section-title">
              Affichage à droite de la note
            </h4>
            <div
              className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--4"
              role="radiogroup"
              aria-label="Affichage dans la liste"
            >
              {Object.values(NOTES_LIST_DISPLAY_MODES).map((mode) => (
                <RadioChip
                  key={mode}
                  name="notes-list-display-mode"
                  label={NOTES_LIST_DISPLAY_LABELS[mode]}
                  checked={draft.listDisplayMode === mode}
                  onChange={() => setDraft((prev) => ({ ...prev, listDisplayMode: mode }))}
                />
              ))}
            </div>
            {displayHelp ? (
              <p className="register-settings-modal__help">{displayHelp}</p>
            ) : null}
          </section>

          <section className="register-settings-modal__section">
            <h4 className="register-settings-modal__section-title">Préférence d'affichage</h4>
            <div
              className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--4"
              role="radiogroup"
              aria-label="Ordre d'affichage des notes"
            >
              {Object.values(NOTES_SORT_ORDERS).map((order) => (
                <RadioChip
                  key={order}
                  name="notes-sort-order"
                  label={NOTES_SORT_ORDER_LABELS[order]}
                  checked={draft.sortOrder === order}
                  onChange={() => setDraft((prev) => ({ ...prev, sortOrder: order }))}
                />
              ))}
            </div>
            {sortHelp ? <p className="register-settings-modal__help">{sortHelp}</p> : null}
          </section>

          <section className="register-settings-modal__section">
            <h4 className="register-settings-modal__section-title">Préférences de scroll</h4>
            <p className="register-settings-modal__help">
              Positionne automatiquement la barre de scroll du détail de la note
              en mode lecture.
            </p>
            <div
              className="entrainement-chip-grid register-settings-modal__radio-grid register-settings-modal__radio-grid--2"
              role="radiogroup"
              aria-label="Préférence de scroll"
            >
              {Object.values(NOTES_SCROLL_ANCHORS).map((anchor) => (
                <RadioChip
                  key={anchor}
                  name="notes-scroll-anchor"
                  label={NOTES_SCROLL_ANCHOR_LABELS[anchor]}
                  checked={draft.scrollAnchor === anchor}
                  onChange={() => setDraft((prev) => ({ ...prev, scrollAnchor: anchor }))}
                />
              ))}
            </div>
          </section>
        </div>

        <div className="delete-word-modal__actions register-settings-modal__actions">
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--cancel"
            onClick={handleReset}
          >
            Réinitialiser
          </button>
          <button
            type="button"
            className="delete-word-modal__btn delete-word-modal__btn--confirm"
            onClick={handleApply}
          >
            Appliquer
          </button>
        </div>
      </div>
    </div>,
    getAppPortalRoot()
  );
}
